import { Command } from 'commander';
import { ConfigService } from '../../services/config.service.js';
import { VaultSyncService } from '../../services/vault-sync.service.js';
import { unlockVault } from './shared.js';

interface SyncOptions {
    pull?: boolean;
    push?: boolean;
    passphrase?: string;
    recoveryKey?: string;
    json?: boolean;
}

export function createVaultSyncCommand(): Command {
    return new Command('sync')
        .description('Sync encrypted vault blobs with Jai1 server')
        .option('--pull', 'Pull encrypted vault from server')
        .option('--push', 'Push encrypted local vault to server')
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, options: SyncOptions) {
            const config = await new ConfigService().load();
            if (!config) throw new Error('Jai1 auth is required. Run `j auth` first.');
            const service = new VaultSyncService();
            const useJson = !!(options.json || this.optsWithGlobals().json);

            if (options.pull) {
                const result = await service.pull(config);
                if (result.keyring) {
                    const { VaultStoreService } = await import('../../services/vault-store.service.js');
                    const store = new VaultStoreService();
                    await store.saveKeyring(result.keyring);
                    for (const item of result.items) {
                        await store.saveEncryptedItem(item);
                    }
                }
                if (useJson) console.log(JSON.stringify({ ...result, applied: !!result.keyring }, null, 2));
                else console.log(`Pulled ${result.items.length} encrypted vault item(s)${result.keyring ? ' into local vault' : ''}.`);
                return;
            }

            const { store } = await unlockVault(options);
            const keyring = await store.loadKeyring();
            const items = await store.listEncryptedItems({ includeDeleted: true });
            const localDevice = await store.loadDevice();
            const device = localDevice ? {
                deviceId: localDevice.deviceId,
                deviceName: localDevice.deviceName,
                publicKey: localDevice.publicKey,
                fingerprint: localDevice.fingerprint,
            } : undefined;
            const result = await service.push(config, { keyring, items, device });
            if (useJson) console.log(JSON.stringify(result, null, 2));
            else console.log(`Synced ${items.length} encrypted vault item(s).`);
        });
}
